import React from "react";
import { useTranslation } from "react-i18next";
import { Card, Typography, Button, Tag, Tooltip, Popconfirm } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from "@ant-design/icons";
import styled from "styled-components";
import moment from "moment-timezone";

import { GRAY_500, GRAY_300, SUCCESS_500, SUCCESS_50, ERROR_500, ERROR_50, BLACK } from "../../constant/colors";

const { Text } = Typography;

const StyledCard = styled(Card)`
  border-radius: 10px;
  border: 1px solid ${GRAY_300};
  transition: all 0.2s;
  min-height: 140px;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  .ant-card-body {
    padding: 20px;
    height: 100%;
    display: flex;
    flex-direction: column;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const PortalInfo = styled.div`
  flex: 1;
`;

const PortalName = styled(Text)`
  font-size: 16px;
  font-weight: 600;
  color: ${BLACK};
  display: block;
  margin-bottom: 4px;
`;

const PortalUrl = styled(Text)`
  font-size: 12px;
  color: ${GRAY_500};
  display: block;
`;

const StatusTag = styled(Tag)`
  border-radius: 6px;
  font-size: 12px;
  padding: 2px 8px;
  border: none;
`;

const ValidTag = styled(StatusTag)`
  background: ${SUCCESS_50};
  color: ${SUCCESS_500};
`;

const InvalidTag = styled(StatusTag)`
  background: ${ERROR_50};
  color: ${ERROR_500};
`;

const CardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const CredentialInfo = styled.div`
  margin-bottom: 8px;
`;

const InfoLabel = styled(Text)`
  font-size: 12px;
  color: ${GRAY_500};
  display: block;
  margin-bottom: 2px;
`;

const InfoValue = styled(Text)`
  font-size: 14px;
  color: ${BLACK};
  display: block;
`;

const LastUpdated = styled(Text)`
  font-size: 12px;
  color: ${GRAY_500};
  display: block;
  margin-top: 8px;
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${GRAY_300};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 16px 0;
`;

const EmptyText = styled(Text)`
  font-size: 14px;
  color: ${GRAY_500};
  margin-bottom: 12px;
`;

const PortalCredentialCard = ({ portal, credential, onAdd, onEdit, onDelete, isDeleting }) => {
  const { t } = useTranslation();

  const hasCredential = !!credential;
  const isValid = credential?.isValid !== false;

  return (
    <StyledCard data-testid={`portal-credential-card-${portal.id}`}>
      <CardHeader>
        <PortalInfo>
          <PortalName>{portal.name}</PortalName>
          {portal.url && <PortalUrl>{portal.url}</PortalUrl>}
        </PortalInfo>
        {hasCredential &&
          (isValid ? (
            <ValidTag icon={<CheckCircleOutlined />}>{t("portal_credentials.valid")}</ValidTag>
          ) : (
            <Tooltip
              title={
                credential.invalidationReason
                  ? t("portal_credentials.invalid_reason", { reason: credential.invalidationReason })
                  : ""
              }
            >
              <InvalidTag icon={<ExclamationCircleOutlined />}>{t("portal_credentials.invalid")}</InvalidTag>
            </Tooltip>
          ))}
      </CardHeader>

      <CardContent>
        {hasCredential ? (
          <>
            <CredentialInfo>
              <InfoLabel>{t("portal_credentials.username")}</InfoLabel>
              <InfoValue>{credential.username}</InfoValue>
            </CredentialInfo>
            <CredentialInfo>
              <InfoLabel>{t("portal_credentials.password")}</InfoLabel>
              <InfoValue>********</InfoValue>
            </CredentialInfo>
            {credential.updatedAt && (
              <LastUpdated>
                {t("portal_credentials.last_updated")}: {moment(credential.updatedAt).format("MMM D, YYYY")}
              </LastUpdated>
            )}
          </>
        ) : (
          <EmptyState>
            <EmptyText>{t("portal_credentials.no_credentials_description")}</EmptyText>
          </EmptyState>
        )}
      </CardContent>

      <CardActions>
        {hasCredential ? (
          <>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={onEdit}
              data-testid={`edit-credential-${portal.id}`}
            >
              {t("edit")}
            </Button>
            <Popconfirm
              title={t("portal_credentials.confirm_delete")}
              description={t("portal_credentials.confirm_delete_description", { portalName: portal.name })}
              onConfirm={onDelete}
              okText={t("portal_credentials.delete")}
              cancelText={t("cancel")}
              okButtonProps={{ danger: true, loading: isDeleting }}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={isDeleting}
                data-testid={`delete-credential-${portal.id}`}
              >
                {t("portal_credentials.delete")}
              </Button>
            </Popconfirm>
          </>
        ) : (
          <Button type="primary" icon={<PlusOutlined />} onClick={onAdd} data-testid={`add-credential-${portal.id}`}>
            {t("portal_credentials.add_credential")}
          </Button>
        )}
      </CardActions>
    </StyledCard>
  );
};

export { PortalCredentialCard };
